import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/contexts/AuthContext'
import { useSnackbar } from '@/hooks/common/useSnackbar'
import { doc, getDoc, setDoc } from 'firebase/firestore'
import { db } from '@/config/firebase'
import { WireInfo } from '@otw/models'

interface UseWireInfoOptions {
  onSuccess?: () => void
  onError?: (error: Error) => void
  dontShowUpdates?: boolean
  enabled?: boolean
}

interface UpdateWireInfoParams {
  data: Partial<Omit<WireInfo, 'id' | 'created_at' | 'created_by'>>
}

// Wire info is stored as a single document with ID 'default'
const WIRE_INFO_DOC_ID = 'default'

export const useWireInfo = (options?: UseWireInfoOptions) => {
  const { showSuccess, showError } = useSnackbar()
  const queryClient = useQueryClient()
  const { currentUser } = useAuth()

  const queryKey = ['wire_info']
  const docRef = doc(db, 'wire_info', WIRE_INFO_DOC_ID)

  const {
    data: wireInfo,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: async (): Promise<WireInfo | null> => {
      const docSnap = await getDoc(docRef)
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() } as WireInfo
      }
      return null
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: options?.enabled !== false,
  })

  const updateWireInfo = useMutation({
    mutationFn: async ({ data }: UpdateWireInfoParams) => {
      const now = new Date().toISOString()
      
      // Get existing document to preserve created_at and created_by
      const existingDoc = await getDoc(docRef)
      const existingData = existingDoc.exists() ? existingDoc.data() : null
      
      const wireInfoData = {
        ...(existingData || {}),
        ...data,
        updated_at: now,
        updated_by: currentUser?.uid || '',
        ...(existingData ? {} : {
          created_at: now,
          created_by: currentUser?.uid || '',
        }),
      }

      await setDoc(docRef, wireInfoData, { merge: true })
      return { id: WIRE_INFO_DOC_ID, ...wireInfoData } as WireInfo
    },
    onSuccess: updatedWireInfo => {
      if (!options?.dontShowUpdates) {
        showSuccess('Wire information updated successfully')
      }

      queryClient.setQueryData(queryKey, updatedWireInfo)

      options?.onSuccess?.()
    },
    onError: err => {
      if (!options?.dontShowUpdates) {
        showError('Error updating wire information')
      }

      console.error('Error updating wire information:', err)
      options?.onError?.(err as Error)
    },
  })

  return {
    wireInfo: wireInfo || null,
    isLoading,
    isError,
    error,
    updateWireInfo: updateWireInfo.mutate,
    updateWireInfoAsync: updateWireInfo.mutateAsync,
    isUpdating: updateWireInfo.isPending,
    refetch,
  }
}

